import type {
  ComboboxData,
  ComboboxItem,
  ComboboxItemGroup,
  ComboboxStringData,
  ComboboxStringItem,
} from "@mantine/core";

type FlatGroupItem = ComboboxItem & { group?: string };
type ComboboxEntry = string | ComboboxItem | ComboboxStringItem;

const isComboboxItemGroup = (item: unknown): item is ComboboxItemGroup =>
  typeof item === "object" &&
  item !== null &&
  "group" in item &&
  "items" in item &&
  Array.isArray((item as ComboboxItemGroup).items);

const getEntryValue = (entry: ComboboxEntry): string =>
  typeof entry === "string" ? entry : entry.value;

/** Mantine rejects duplicate option values across the entire data tree. */
const dedupeComboboxData = (data: ComboboxData): ComboboxData => {
  const seen = new Set<string>();
  const result: (string | ComboboxItem | ComboboxItemGroup)[] = [];

  for (const entry of data) {
    if (typeof entry === "string") {
      if (seen.has(entry)) continue;
      seen.add(entry);
      result.push(entry);
      continue;
    }

    if (isComboboxItemGroup(entry)) {
      const items = entry.items.filter(item => {
        const value = getEntryValue(item as ComboboxEntry);
        if (typeof value !== "string" || seen.has(value)) return false;
        seen.add(value);
        return true;
      });

      if (items.length > 0) {
        result.push({ ...entry, items });
      }
      continue;
    }

    // Mantine requires a string value; drop options with null/undefined/object values
    if (typeof entry.value !== "string" || seen.has(entry.value)) continue;
    seen.add(entry.value);
    result.push(entry);
  }

  return result;
};

/**
 * Converts Mantine v6-style flat options (`{ label, value, group }`) to v7
 * grouped combobox data (`{ group, items: [...] }`).
 */
export const groupComboboxItems = (items: FlatGroupItem[]): ComboboxData => {
  if (!items?.length) return [];

  const hasGroups = items.some(item => item.group != null && item.group !== "");
  if (!hasGroups) {
    return dedupeComboboxData(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- omitted from the rest props on purpose
      items.map(({ group: _group, ...item }) => item),
    );
  }

  const order: string[] = [];
  const map = new Map<string, ComboboxItem[]>();

  for (const { group = "", ...item } of items) {
    const groupName = group || "Other";
    if (!map.has(groupName)) {
      map.set(groupName, []);
      order.push(groupName);
    }
    map.get(groupName)!.push(item);
  }

  return dedupeComboboxData(
    order.map(group => ({ group, items: map.get(group)! })),
  );
};

/**
 * Ensures data passed to Mantine 7 Select/MultiSelect is valid.
 * Flat items must not use a bare `group` property (v7 treats that as a group container).
 */
export const normalizeSelectData = (
  data: FlatGroupItem[] | ComboboxData | undefined,
): ComboboxData => {
  if (!data?.length) return [];
  if (typeof data[0] === "string") {
    return dedupeComboboxData(data as ComboboxData);
  }
  const normalized = isComboboxItemGroup(data[0])
    ? (data as ComboboxData)
    : groupComboboxItems(data as FlatGroupItem[]);
  return dedupeComboboxData(normalized);
};

/** Ensures Autocomplete data has unique option values (including string[]). */
export const normalizeAutocompleteData = (
  data: ComboboxStringData | undefined,
): ComboboxStringData => {
  if (!data?.length) return [];
  return dedupeComboboxData(data as ComboboxData) as ComboboxStringData;
};

/** Mantine 7 Select only resolves labels when value is a string. */
export const normalizeSelectValue = (value: unknown): string | null => {
  if (value == null || value === "") return null;
  if (typeof value === "object") return null;
  return String(value);
};
